"use client";

import React, { Suspense, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { PostHogProvider as PHProvider } from "posthog-js/react";

import { posthog, isPosthogEnabled } from "@/lib/posthog";

/**
 * Captures a $pageview on every pathname or search-param change.
 * PostHog is initialised by instrumentation-client.ts before React mounts,
 * so there is no init/capture race here.
 * Must be wrapped in <Suspense> because useSearchParams() opts the
 * component out of static rendering in Next.js App Router.
 */
function PostHogPageView() {
	const pathname = usePathname();
	const searchParams = useSearchParams();

	useEffect(() => {
		if (!isPosthogEnabled()) {
			return;
		}
		posthog.capture("$pageview", {
			$current_url:
				typeof window !== "undefined" ? window.location.href : pathname,
		});
	}, [pathname, searchParams]);

	return null;
}

/**
 * Wraps children with the PostHog React provider so that any component in the
 * tree can call usePostHog() or useFeatureFlagEnabled() if needed.
 * Initialisation is handled by instrumentation-client.ts, not here.
 */
export default function PostHogProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	return (
		<PHProvider client={posthog}>
			<Suspense fallback={null}>
				<PostHogPageView />
			</Suspense>
			{children}
		</PHProvider>
	);
}
